// Persona editor drawer body (personas.md). Create writes agent + display
// name + selections; edit revises the spec and PATCHes a changed display
// name. Both fields stay editable. When the page is unscoped the create form
// starts with an owning-agent Select.
//
// Every provider, model, and voice choice renders from the
// `persona_provider_policy` projection on GET /v1/me (#1441) — the same
// enabled-set computation the server validates saves against, so the form
// never offers what a save would reject. No provider literals live here.
import type { SyntheticEvent } from "react";
import { useState } from "react";

import type { components } from "../../api/schema";
import { docUrl } from "../../lib/docLinks";
import { requestErrorDescription } from "../../lib/errors";
import { Link } from "@tanstack/react-router";

import {
  Button,
  HelpTip,
  Inline,
  InlineError,
  Link as DocumentationLink,
  ProviderLogo,
  Select,
  Stack,
  Text,
  TextInput,
} from "../../ui";
import {
  buildPersonaSubmission,
  defaultModelLabel,
  defaultVoiceRefFor,
  personaDraftFromForm,
  providerById,
  voiceOptionLabel,
  type PersonaFormDraft,
  type PersonaFormSubmission,
  type PersonaVariableValues,
} from "./personaSpec";
import { PersonaVariablesSection } from "./PersonaVariablesSection";
import { unicodeCaseFold } from "../../lib/unicodeCaseFold";
import type { VariableResponse } from "../../lib/variableCascade";

type PersonaPhoneEndpointMode =
  components["schemas"]["PersonaPhoneEndpointMode"];
type PhoneNumberResponse = components["schemas"]["PhoneNumberResponse"];
type PersonaProviderPolicy =
  components["schemas"]["PersonaProviderPolicyResponse"];
type PersonaProvider = components["schemas"]["PersonaProviderResponse"];
type PersonaResponse = components["schemas"]["PersonaResponse"];

export interface PersonaAgentOption {
  id: string;
  label: string;
}

export interface PersonaEditorProps {
  // Owning-agent choices for the unscoped create form; omit to hide the
  // Select (agent-scoped create, or edit — a persona never changes agents).
  agentOptions?: PersonaAgentOption[];
  error: string | null;
  isPending: boolean;
  onCancel: () => void;
  onSubmit: (submission: PersonaFormSubmission) => Promise<void>;
  // A GET /v1/phone-numbers load failure; the pinned branch says so rather
  // than rendering an empty picker that looks like an empty pool.
  canBuyNumbers: boolean;
  variableDeclarations?: readonly VariableResponse[];
  phoneNumbersError: string | null;
  isPhoneNumbersLoading: boolean;
  // null = create; a resolved persona = edit.
  persona: PersonaResponse | null;
  // The org's synced telephony inventory (GET /v1/phone-numbers). Only
  // `available` rows are selectable — save-time validation rejects the rest,
  // so the picker never offers what a save would reject.
  phoneNumbers: readonly PhoneNumberResponse[];
  // The deployment's enabled persona providers from GET /v1/me; the
  // container passes the fail-closed empty policy while /v1/me is pending.
  providerPolicy: PersonaProviderPolicy;
  // Pre-fills the owning agent when the page carries an agent scope.
  scopedAgentId?: string;
}

export function PersonaEditor(props: PersonaEditorProps) {
  const subjectKey = props.persona?.id ?? `new:${props.scopedAgentId ?? ""}`;
  return <PersonaEditorForm key={subjectKey} {...props} />;
}

function PersonaEditorForm({
  agentOptions,
  error,
  isPending,
  isPhoneNumbersLoading,
  onCancel,
  onSubmit,
  persona,
  canBuyNumbers,
  variableDeclarations,
  phoneNumbers,
  phoneNumbersError,
  providerPolicy,
  scopedAgentId,
}: PersonaEditorProps) {
  const [draft, setDraft] = useState(() =>
    personaDraftFromForm(persona, providerPolicy, scopedAgentId),
  );
  const [variableValues, setVariableValues] = useState<PersonaVariableValues>(
    {},
  );
  const [localError, setLocalError] = useState<string | null>(null);

  const showAgentSelect = persona === null && agentOptions !== undefined;
  const selectedLlm = providerById(
    providerPolicy.llm_providers,
    draft.llmProvider,
  );
  const modelLabel =
    selectedLlm === undefined ? null : defaultModelLabel(selectedLlm);

  async function handleSubmit(event: SyntheticEvent<HTMLFormElement>) {
    event.preventDefault();
    const submission = buildPersonaSubmission(
      draft,
      providerPolicy,
      variableValues,
    );
    if (!submission.ok) {
      setLocalError(submission.error);
      return;
    }
    setLocalError(null);
    try {
      await onSubmit(submission.value);
    } catch (caught) {
      setLocalError(requestErrorDescription(caught));
    }
  }

  return (
    <Stack gap={4}>
      <form
        onSubmit={(event) => {
          void handleSubmit(event);
        }}
      >
        <Stack gap={4}>
          {showAgentSelect ? (
            <Select
              label="Agent"
              required
              value={draft.agentId}
              onChange={(event) => {
                setVariableValues({});
                setDraft({
                  ...draft,
                  agentId: event.currentTarget.value,
                });
              }}
            >
              <option disabled value="">
                Select an agent
              </option>
              {agentOptions.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.label}
                </option>
              ))}
            </Select>
          ) : null}
          <TextInput
            label="Display name"
            required
            value={draft.displayName}
            onChange={(event) => {
              setDraft({
                ...draft,
                displayName: event.currentTarget.value,
              });
            }}
          />
          {providerPolicy.llm_providers.length === 0 ? (
            <Stack gap={1}>
              <Text variant="caps">LLM provider</Text>
              <Text variant="muted">
                No LLM provider is configured. Ask your administrator.
              </Text>
            </Stack>
          ) : (
            <Select
              label="LLM provider"
              required
              value={draft.llmProvider}
              onChange={(event) => {
                setDraft({
                  ...draft,
                  llmProvider: event.currentTarget.value,
                });
              }}
            >
              {providerOptions(providerPolicy.llm_providers, draft.llmProvider)}
            </Select>
          )}
          {modelLabel !== null ? (
            <Text variant="muted">
              Model: {modelLabel} (deployment default)
            </Text>
          ) : null}
          <Select
            label="Who speaks first"
            description="Defaults to Agent."
            help={
              <HelpTip label="About who speaks first">
                {"Agent lets the agent greet first; Persona opens the call " +
                  "in character using the scenario."}
              </HelpTip>
            }
            value={draft.firstSpeaker}
            onChange={(event) => {
              setDraft({
                ...draft,
                firstSpeaker:
                  event.currentTarget.value === "persona" ? "persona" : "agent",
              });
            }}
          >
            <option value="agent">Agent</option>
            <option value="persona">Persona</option>
          </Select>
          {draft.firstSpeaker === "persona" ? (
            <TextInput
              label="Opening line"
              description="Optional. Leave blank to generate the opening."
              maxLength={150}
              value={draft.openingLine}
              onChange={(event) => {
                setDraft({ ...draft, openingLine: event.currentTarget.value });
              }}
            />
          ) : null}
          <TextInput
            label="Barge-in threshold (words)"
            description="Optional, 1–8 words; blank uses the default."
            help={
              <HelpTip label="About the barge-in threshold">
                {"Shorter callee speech does not interrupt a speaking " +
                  "persona. When the persona is silent, the callee's " +
                  "first word starts a normal turn."}
              </HelpTip>
            }
            type="number"
            min={1}
            max={8}
            step={1}
            value={draft.bargeInMinWords}
            onChange={(event) => {
              setDraft({
                ...draft,
                bargeInMinWords: event.currentTarget.value,
              });
            }}
          />
          <VoiceSection
            draft={draft}
            setDraft={setDraft}
            voiceProviders={providerPolicy.voice_providers}
            voiceSupported={providerPolicy.voice_supported}
          />
          <Select
            help={
              <HelpTip
                docHref={docUrl("guides/author-a-persona")}
                docLabel="Author a Persona"
                label="About number selection"
              >
                {
                  "Auto assigns an available pool number at runtime. Pinned from pool keeps this Persona on one pool number for every run."
                }
              </HelpTip>
            }
            label="Number selection"
            required
            value={draft.mode}
            onChange={(event) => {
              setDraft({
                ...draft,
                mode: event.currentTarget.value as PersonaPhoneEndpointMode,
              });
            }}
          >
            <option value="auto">Auto</option>
            <option value="pinned">Pinned from pool</option>
          </Select>
          {draft.mode === "pinned" ? (
            <PinnedNumberSection
              draft={draft}
              isLoading={isPhoneNumbersLoading}
              canBuyNumbers={canBuyNumbers}
              loadError={phoneNumbersError}
              phoneNumbers={phoneNumbers}
              setDraft={setDraft}
            />
          ) : null}
          <PersonaVariablesSection
            key={draft.agentId}
            agentId={draft.agentId}
            declarations={variableDeclarations}
            isPending={isPending}
            personaId={persona?.id}
            spec={persona?.current_revision.spec ?? null}
            values={variableValues}
            onValueChange={(key, value) => {
              setVariableValues((current) => {
                const next = Object.fromEntries(
                  Object.entries(current).filter(
                    ([currentKey]) =>
                      unicodeCaseFold(currentKey) !== unicodeCaseFold(key),
                  ),
                );
                return { ...next, [key]: value };
              });
            }}
          />
          {(localError ?? error) ? (
            <InlineError>{localError ?? error}</InlineError>
          ) : null}
          <Inline justify="end">
            <Button
              disabled={isPending}
              type="button"
              variant="secondary"
              onClick={onCancel}
            >
              Cancel
            </Button>
            <Button disabled={isPending} type="submit">
              {isPending ? "Saving..." : "Save"}
            </Button>
          </Inline>
        </Stack>
      </form>
    </Stack>
  );
}

// The pinned-number picker. The persisted value is unchanged — a persona
// still stores the `endpoint_pool` row id as `pool_number_ref` — but a human
// picks the E.164 address instead of typing an opaque UUID.
//
// Shared Marker numbers are excluded even though GET /v1/phone-numbers now
// returns them: the API refuses a `pool_number_ref` this org does not own, so
// offering one would be an option that always fails to save. `Auto` already
// leases from the shared pool, which is how a persona reaches those numbers.
//
// `endpoint_pool` is a synced mirror of carrier holdings, so this deployment
// can legitimately have none. The "Buy more numbers" action renders only where
// Marker can actually reach a carrier (GET /v1/me
// `number_provisioning_enabled`); elsewhere the empty state stays truthful and
// names an administrator instead.
function PinnedNumberSection({
  canBuyNumbers,
  draft,
  isLoading,
  loadError,
  phoneNumbers,
  setDraft,
}: {
  canBuyNumbers: boolean;
  draft: PersonaFormDraft;
  isLoading: boolean;
  loadError: string | null;
  phoneNumbers: readonly PhoneNumberResponse[];
  setDraft: (draft: PersonaFormDraft) => void;
}) {
  const savedRef = draft.poolNumberRef;
  const pinnable = phoneNumbers.filter((number) => !number.shared);
  if (loadError !== null) {
    return (
      <Stack gap={1}>
        <Text variant="caps">Phone number</Text>
        <InlineError>{loadError}</InlineError>
        {savedRef.length > 0 ? (
          <Text variant="muted">Saved number: {savedRef}</Text>
        ) : null}
      </Stack>
    );
  }
  if (isLoading) {
    return (
      <Stack gap={1}>
        <Text variant="caps">Phone number</Text>
        <Text variant="muted">Loading numbers...</Text>
      </Stack>
    );
  }
  if (pinnable.length === 0) {
    return (
      <Stack gap={1}>
        <Text variant="caps">Phone number</Text>
        <Text variant="muted">
          {canBuyNumbers
            ? "No phone numbers yet for this organization."
            : "No phone numbers are synced for this organization. Ask your administrator, or use Auto."}
        </Text>
        <BuyMoreNumbersLink enabled={canBuyNumbers} />
        {savedRef.length > 0 ? (
          <Text variant="muted">Saved number: {savedRef}</Text>
        ) : null}
      </Stack>
    );
  }
  // A saved ref that is no longer in the pool stays visible (disabled, raw
  // id) so an unrelated edit can never silently drop the persisted value.
  const known = pinnable.some((number) => number.id === savedRef);
  return (
    <Select
      label="Phone number"
      required
      value={savedRef}
      onChange={(event) => {
        setDraft({ ...draft, poolNumberRef: event.currentTarget.value });
      }}
    >
      {savedRef.length === 0 ? (
        <option disabled value="">
          Select a number
        </option>
      ) : null}
      {savedRef.length > 0 && !known ? (
        <option disabled value={savedRef}>
          {`${savedRef} (no longer in this pool)`}
        </option>
      ) : null}
      {pinnable.map((number) => (
        <option
          key={number.id}
          disabled={number.status !== "available"}
          value={number.id}
        >
          {number.status === "available"
            ? number.address
            : `${number.address} (${number.status})`}
        </option>
      ))}
    </Select>
  );
}

// Renders only where provisioning genuinely exists. On an install that cannot
// reach a carrier there is nothing to buy, so no affordance is shown at all.
function BuyMoreNumbersLink({ enabled }: { enabled: boolean }) {
  if (!enabled) {
    return null;
  }
  return <Link to="/settings/phone-numbers">Buy more numbers</Link>;
}

// The simulated-voice block: provider Select, then that provider's catalog
// voices. Selection is catalog-only (founder ruling 2026-07-31) — there is
// no free-text voice id, because a voice outside the catalog has no credit
// rate and no provider quota binding, and a save would reject it. A provider
// whose catalog is empty here says so instead of offering a text box. An
// empty provider list (e.g. air-gapped) renders a truthful unavailable state
// — no dropdowns over capability that does not exist here. The empty state
// splits on `voice_supported` (GET /v1/me): a supported deployment links its
// operator directly to provider configuration; where the deployment permits
// no voice vendor at all, no credential ever produces a provider, so the copy
// names no action.
function VoiceSection({
  draft,
  setDraft,
  voiceProviders,
  voiceSupported,
}: {
  draft: PersonaFormDraft;
  setDraft: (draft: PersonaFormDraft) => void;
  voiceProviders: readonly PersonaProvider[];
  voiceSupported: boolean;
}) {
  if (voiceProviders.length === 0) {
    return (
      <Stack gap={1}>
        <Text variant="caps">Voice</Text>
        <Text variant="muted">
          {voiceSupported ? (
            <>
              No voice provider is configured. Configure one in the{" "}
              <DocumentationLink
                href={docUrl("deploy/bring-your-own-models")}
                rel="noreferrer"
                target="_blank"
              >
                deployment documentation
              </DocumentationLink>
              .
            </>
          ) : (
            "Voice is unavailable in this deployment."
          )}
        </Text>
      </Stack>
    );
  }
  const selected = providerById(voiceProviders, draft.voiceProvider);
  const savedRef = draft.voiceRef.trim();
  const orphanedRef =
    selected !== undefined &&
    savedRef.length > 0 &&
    !selected.voices.some((voice) => voice.voice_ref === savedRef);
  return (
    <>
      <Select
        label="Voice provider"
        required
        value={draft.voiceProvider}
        onChange={(event) => {
          const provider = providerById(
            voiceProviders,
            event.currentTarget.value,
          );
          setDraft({
            ...draft,
            voiceProvider: event.currentTarget.value,
            voiceRef:
              provider === undefined ? "" : defaultVoiceRefFor(provider),
          });
        }}
      >
        {providerOptions(voiceProviders, draft.voiceProvider)}
      </Select>
      {selected !== undefined && selected.voices.length > 0 ? (
        <Select
          label="Voice"
          required
          value={draft.voiceRef}
          onChange={(event) => {
            setDraft({ ...draft, voiceRef: event.currentTarget.value });
          }}
        >
          {/* A saved voice this deployment no longer offers stays visible and
              disabled, so the edit form never silently swaps the persisted
              selection for another voice. */}
          {orphanedRef ? (
            <option disabled value={savedRef}>
              {`${savedRef} (no longer available)`}
            </option>
          ) : null}
          {selected.voices.map((voice) => (
            <option key={voice.voice_ref} value={voice.voice_ref}>
              {voiceOptionLabel(voice)}
            </option>
          ))}
        </Select>
      ) : null}
      {selected !== undefined && selected.voices.length === 0 ? (
        <Stack gap={1}>
          <Text variant="caps">Voice</Text>
          <Text variant="muted">
            No voice is configured for this provider. Ask your administrator.
          </Text>
        </Stack>
      ) : null}
    </>
  );
}

// Provider rows with the design-system identity glyph. A saved provider no
// longer enabled in this deployment stays visible (disabled, raw token) so
// the edit form never silently relabels or drops the persisted value.
function providerOptions(
  providers: readonly PersonaProvider[],
  selectedId: string,
) {
  const known = providers.some(
    (provider) => provider.provider_id === selectedId,
  );
  return (
    <>
      {selectedId.length > 0 && !known ? (
        <option disabled value={selectedId}>
          {`${selectedId} (not available in this deployment)`}
        </option>
      ) : null}
      {providers.map((provider) => (
        <option
          key={provider.provider_id}
          label={provider.display_name}
          value={provider.provider_id}
        >
          <ProviderLogo
            label={provider.display_name}
            providerId={provider.provider_id}
          />{" "}
          {provider.display_name}
        </option>
      ))}
    </>
  );
}
